import { formatDate, ISODateFormat } from '@northfork/basics'
import {
  BalanceToAdjust,
  BalanceToAdjustKey,
  balanceToAdjustKeys,
} from '../model/balance-to-adjust.model'
import { Currency } from '../model/currency.model'
import { InventoryType } from '../model/inventory-type.model'
import {
  LoanFeeType,
  LoanFeeTypeKey,
  loanFeeTypeKeys,
} from '../model/loan-fee-type.model'
import { CashEntryMatchRow } from '../model/single-transaction/cash-entry/cash-entry-match.model'
import { CashEntryTransactionDetails } from '../model/single-transaction/cash-entry/cash-entry-transaction-details'
import { CashEntryTransaction } from '../model/single-transaction/cash-entry/cash-entry-transaction.model'
import {
  CashEntryInputModel,
  CashEntryModel,
} from '../model/single-transaction/cash-entry/cash-entry.model'
import { ExternalUnmatchedTransaction } from '../model/single-transaction/external-unmatched-transaction'
import { FeeTypeData } from '../model/single-transaction/feeTypeData.model'
import { HistoricalBalanceData } from '../model/single-transaction/popup-input.model'
import { TransactionMatch } from '../model/single-transaction/transaction-match'
import { TransactionTypeEnum } from '../model/transaction-type.model'
import {
  CURRENCY_ONLY_TRANSACTION_TYPES,
  getTransactionTypeKeyFromCode,
} from './common-utils'
import { getCurrencyFromId, getDefaultCurrency } from './currency-utils'

/**
 * Function to Create Cash entry
 */
export function generateCashEntry(reconDate: string): CashEntryModel[] {
  const currency = getDefaultCurrency()
  return [
    {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Account ID',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'category',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value: TransactionTypeEnum['CASH_TRANSFER'],
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: reconDate,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: reconDate,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: reconDate,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset ID',
      value: currency.cusip,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'currency',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Currency',
      value: currency.code,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'amount',
      validityStatus: 'invalid',
      type: 'number',
      isEditable: true,
      field: 'Amount',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
  ]
}

/**
 * Function to create transaction from custody external transaction
 */
export function createCashEntryFromExternalTransactionCustody(
  externalTransaction: ExternalUnmatchedTransaction,
  currencyList: Currency[],
  accountName: string,
  isAggregate: boolean,
): CashEntryModel[] {
  // eslint-disable-next-line prefer-const
  let [cusip, currency] = fetchExternalTransactionCurrencyValues(
    externalTransaction,
    currencyList,
  )
  let cwCusip = cusip
  let cwCurrency = currency
  if (
    externalTransaction.currencyId &&
    externalTransaction.secMasterCurrencyId &&
    externalTransaction.currencyId != externalTransaction.secMasterCurrencyId
  ) {
    ;[cwCusip, cwCurrency] = fetchExternalTransactionSecMasterCurrencyValues(
      externalTransaction,
      currencyList,
    )
    cusip = cwCusip
  }

  return [
    {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: isAggregate,
      field: 'Account ID',
      value: accountName,
      custody: accountName,
      trading: '',
      errorMessage: '',
    },
    {
      id: 'category',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value: externalTransaction.tranType
        ? TransactionTypeEnum[externalTransaction.tranType]
        : TransactionTypeEnum['CASH_TRANSFER'],
      custody: externalTransaction.tranType
        ? TransactionTypeEnum[externalTransaction.tranType]
        : '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'dataSource',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Data Source',
      value: '',
      custody: externalTransaction.dataSource ?? '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: externalTransaction.entryDate,
      custody: externalTransaction.entryDate,
      trading: '',
      errorMessage: '',
    },
    {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: externalTransaction.settleDate,
      custody: externalTransaction.settleDate,
      trading: '',
      errorMessage: '',
    },
    {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: externalTransaction.postDate,
      custody: externalTransaction.postDate,
      trading: '',
      errorMessage: '',
    },
    {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: CURRENCY_ONLY_TRANSACTION_TYPES.includes(
        TransactionTypeEnum[externalTransaction.tranType],
      )
        ? false
        : true,
      field: 'Asset ID',
      value: cusip ?? '',
      custody: externalTransaction?.assetId,
      trading: '',
      errorMessage: '',
    },
    {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: externalTransaction?.cwanIdentifier ?? '',
      custody: externalTransaction?.cwanIdentifier ?? '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'currency',
      validityStatus: 'valid',
      type: 'string',
      isEditable: CURRENCY_ONLY_TRANSACTION_TYPES.includes(
        TransactionTypeEnum[externalTransaction.tranType],
      )
        ? true
        : false,
      field: 'Currency',
      value: cwCurrency ? cwCurrency.code : '',
      custody: currency ? currency.code : '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'amount',
      validityStatus: externalTransaction.amount ? 'valid' : 'invalid',
      type: 'number',
      isEditable: true,
      field: 'Amount',
      value: externalTransaction.amount
        ? Number(externalTransaction.amount).toString()
        : '',
      custody: externalTransaction.amount
        ? Number(externalTransaction.amount).toString()
        : '',
      trading: '',
      errorMessage: externalTransaction.amount ? '' : 'Amount must be set',
    },
  ]
}

/**
 * Function to create transaction from historical balance
 */
export function createCashEntryFromHistoricalBalance(
  pos: HistoricalBalanceData,
  tranTypeCode?: string,
): CashEntryModel[] {
  const tranTypeKey = getTransactionTypeKeyFromCode(tranTypeCode ?? 'TRN')
  return [
    {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Account ID',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'category',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value: TransactionTypeEnum[tranTypeKey],
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: pos.date,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: pos.date,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: pos.date,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset ID',
      value: pos.assetId,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'currency',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Currency',
      value: pos.currencyCode,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'amount',
      validityStatus: 'invalid',
      type: 'number',
      isEditable: true,
      field: 'Amount',
      value: Number(pos.unitDifference).toString(),
      custody: '',
      trading: '',
      errorMessage: 'Amount must be set',
    },
  ]
}

/**
 * Function to create transaction from trading external transaction
 */
export function createCashEntryFromExternalTransactionTrading(
  externalTransaction: ExternalUnmatchedTransaction,
  currencyList: Currency[],
  accountName: string,
  isAggregate: boolean,
): CashEntryModel[] {
  // eslint-disable-next-line prefer-const
  let [cusip, currency] = fetchExternalTransactionCurrencyValues(
    externalTransaction,
    currencyList,
  )
  let cwCusip = cusip
  let cwCurrency = currency
  if (
    externalTransaction.currencyId &&
    externalTransaction.secMasterCurrencyId &&
    externalTransaction.currencyId != externalTransaction.secMasterCurrencyId
  ) {
    ;[cwCusip, cwCurrency] = fetchExternalTransactionSecMasterCurrencyValues(
      externalTransaction,
      currencyList,
    )
    cusip = cwCusip
  }

  return [
    {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: isAggregate,
      field: 'Account ID',
      value: accountName,
      custody: '',
      trading: accountName,
      errorMessage: '',
    },
    {
      id: 'category',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value: externalTransaction.tranType
        ? TransactionTypeEnum[externalTransaction.tranType]
        : TransactionTypeEnum['CASH_TRANSFER'],
      trading: externalTransaction.tranType
        ? TransactionTypeEnum[externalTransaction.tranType]
        : '',
      custody: '',
      errorMessage: '',
    },
    {
      id: 'dataSource',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Data Source',
      value: '',
      custody: '',
      trading: externalTransaction.dataSource ?? '',
      errorMessage: '',
    },
    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: externalTransaction.entryDate,
      trading: externalTransaction.entryDate,
      custody: '',
      errorMessage: '',
    },
    {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: externalTransaction.settleDate,
      trading: externalTransaction.settleDate,
      custody: '',
      errorMessage: '',
    },
    {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: externalTransaction.postDate,
      trading: externalTransaction.postDate,
      custody: '',
      errorMessage: '',
    },
    {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: CURRENCY_ONLY_TRANSACTION_TYPES.includes(
        TransactionTypeEnum[externalTransaction.tranType],
      )
        ? false
        : true,
      field: 'Asset ID',
      value: cusip ?? '',
      trading: externalTransaction?.assetId,
      custody: '',
      errorMessage: '',
    },
    {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: externalTransaction?.cwanIdentifier ?? '',
      trading: externalTransaction?.cwanIdentifier ?? '',
      custody: '',
      errorMessage: '',
    },
    {
      id: 'currency',
      validityStatus: 'valid',
      type: 'string',
      isEditable: CURRENCY_ONLY_TRANSACTION_TYPES.includes(
        TransactionTypeEnum[externalTransaction.tranType],
      )
        ? true
        : false,
      field: 'Currency',
      value: cwCurrency ? cwCurrency.code : '',
      trading: currency ? currency.code : '',
      custody: '',
      errorMessage: '',
    },
    {
      id: 'amount',
      validityStatus: externalTransaction.amount ? 'valid' : 'invalid',
      type: 'number',
      isEditable: true,
      field: 'Amount',
      value: externalTransaction.amount
        ? Number(externalTransaction.amount).toString()
        : '',
      trading: externalTransaction.amount
        ? Number(externalTransaction.amount).toString()
        : '',
      custody: '',
      errorMessage: externalTransaction.amount ? '' : 'Amount must be set',
    },
  ]
}

/**
 * Function to edit Cash entry data
 */
export function editCashEntryFromTransaction(
  cashEntryDetails: CashEntryTransactionDetails,
  currencyList: Currency[],
  accountName: string,
  matchCustody?: TransactionMatch,
  matchTrading?: TransactionMatch,
  accountNameAgg?: string,
  combinedAmountCustody?: string,
  combinedAmountTrading?: string,
  tradingAccountName?: string,
): CashEntryModel[] {
  const cusip = cashEntryDetails?.securityMasterData.cusip
  const cwanIdentifier = cashEntryDetails.transactionData.cwanIdentifier
  const cashEntry = cashEntryDetails.transactionData
  const currencyCode =
    getCurrencyFromId(
      cashEntryDetails?.securityMasterData?.currencyId,
      currencyList,
    )?.code ?? ''

  const custodyCurrencyCode =
    getCurrencyFromId(matchCustody?.currencyId, currencyList)?.code ?? ''
  const tradingCurrencyCode =
    getCurrencyFromId(matchTrading?.currencyId, currencyList)?.code ?? ''

  return [
    {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Account ID',
      value: accountName,
      custody: matchCustody ? accountNameAgg ?? accountName : '',
      trading: matchTrading ? tradingAccountName ?? accountName : '',
      errorMessage: '',
    },
    {
      id: 'dataSource',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Data Source',
      value: '',
      custody: matchCustody?.dataSource ?? '',
      trading: matchTrading?.dataSource ?? '',
      errorMessage: '',
    },
    {
      id: 'category',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value: TransactionTypeEnum[cashEntry.tranType],
      custody: matchCustody?.tranType
        ? TransactionTypeEnum[matchCustody.tranType]
        : '',
      trading: matchTrading?.tranType
        ? TransactionTypeEnum[matchTrading.tranType]
        : '',
      errorMessage: '',
    },
    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: cashEntry.entryDate,
      custody: matchCustody?.entryDate,
      trading: matchTrading?.entryDate,
      errorMessage: '',
    },
    {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: cashEntry.settleDate,
      custody: matchCustody?.settleDate,
      trading: matchTrading?.settleDate,
      errorMessage: '',
    },
    {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: cashEntry.postDate,
      custody: matchCustody?.postDate,
      trading: matchTrading?.postDate,
      errorMessage: '',
    },
    {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset ID',
      value: cusip,
      custody: matchCustody?.assetId,
      trading: matchTrading?.assetId,
      errorMessage: '',
    },
    {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: cwanIdentifier ?? '',
      custody: matchCustody?.cwanIdentifier,
      trading: matchTrading?.cwanIdentifier,
      errorMessage: '',
    },
    {
      id: 'currency',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Currency',
      value: currencyCode,
      custody: custodyCurrencyCode,
      trading: tradingCurrencyCode,
      errorMessage: '',
    },
    {
      id: 'amount',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Amount',
      value: cashEntry.amount ? Number(cashEntry.amount).toString() : '',
      custody: matchCustody?.amount
        ? Number(matchCustody?.amount).toString()
        : combinedAmountCustody,
      trading: matchTrading?.amount
        ? Number(matchTrading?.amount).toString()
        : combinedAmountTrading,
      errorMessage: '',
    },
  ]
}

/**
 * Function to edit Cash entry data
 */
export function createCashEntryFromCashEntryInputModel(
  input: CashEntryInputModel,
): CashEntryModel[] {
  // const currency = getCurrencyFromCashSecurityId(input.secId, currencyList)
  return [
    {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Account ID',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'category',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value:
        TransactionTypeEnum[
          // Defaults to coupon because this is primarily used by coupon transactions (for now)
          getTransactionTypeKeyFromCode(input?.tranTypeCode ?? 'CPN')
        ],
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: input?.entryDate ?? formatDate(new Date(), ISODateFormat) ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: input?.settleDate ?? formatDate(new Date(), ISODateFormat) ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: input?.postDate ?? formatDate(new Date(), ISODateFormat) ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Asset ID',
      value: input?.securityId ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'currency',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Currency',
      value: input?.currency ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    {
      id: 'amount',
      validityStatus: input.amount ? 'valid' : 'invalid',
      type: 'number',
      isEditable: true,
      field: 'Amount',
      value: `${Number(input?.amount ?? 0).toFixed(2)}`,
      custody: '',
      trading: '',
      errorMessage: 'Amount must be set',
    },
  ]
}

export type AdvancedRowsTransaction = Pick<
  CashEntryTransaction,
  'accountFeeType' | 'inventoryType' | 'dataMap'
>

function resolveBalanceToAdjust(balanceToAdjust?: string): string {
  if (balanceToAdjust == null) {
    return ''
  }
  // An external transaction may carry a label or an unrecognised value; only a known key is mapped.
  return balanceToAdjustKeys.includes(balanceToAdjust as BalanceToAdjustKey)
    ? BalanceToAdjust[balanceToAdjust as BalanceToAdjustKey]
    : balanceToAdjust
}

function resolveLoanFeeType(loanFeeType?: string): string {
  if (loanFeeType == null) {
    return ''
  }
  // Only a known enum key resolves to its label; guard with loanFeeTypeKeys.includes so a custom
  // (fee-master) name that collides with an inherited prototype property (e.g. 'toString') isn't
  // read off Object.prototype by the bracket lookup — it passes through unchanged (GAL-5173).
  return loanFeeTypeKeys.includes(loanFeeType as LoanFeeTypeKey)
    ? LoanFeeType[loanFeeType as LoanFeeTypeKey]
    : loanFeeType
}

/**
 * Function to generateAdvancedDataRows for create cash entry
 */
export function generateAdvancedDataRows(
  feeTypeData: FeeTypeData[],
  currentCategory: string,
  transaction?: AdvancedRowsTransaction,
  custodyInventory?: string,
  tradingInventory?: string,
  prepaidInterest?: number,
  custodyPrepaidInterest?: number,
  tradingPrepaidInterest?: number,
): CashEntryModel[] {
  const rows: CashEntryModel[] = []
  if (
    CURRENCY_ONLY_TRANSACTION_TYPES.includes(
      currentCategory as TransactionTypeEnum,
    )
  ) {
    rows.push({
      id: 'feeType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Fee Type',
      value:
        transaction?.accountFeeType && feeTypeData.length > 0
          ? feeTypeData.filter(
              feeType => Number(feeType.id) == transaction.accountFeeType,
            )[0]?.name
          : '',
      custody: '',
      trading: '',
      errorMessage: '',
    })
  } else {
    rows.push(
      {
        id: 'inventoryType',
        validityStatus: 'valid',
        type: 'string',
        isEditable: true,
        field: 'Inventory Type',
        value: transaction?.inventoryType
          ? InventoryType[
              transaction.inventoryType as keyof typeof InventoryType
            ]
          : '',
        custody: custodyInventory,
        trading: tradingInventory,
        errorMessage: '',
      },
      {
        id: 'feeType',
        validityStatus: 'valid',
        type: 'string',
        isEditable: true,
        field: 'Fee Type',
        value:
          transaction?.accountFeeType && feeTypeData.length > 0
            ? feeTypeData.filter(
                feeType => Number(feeType.id) == transaction.accountFeeType,
              )[0].name
            : '',
        custody: '',
        trading: '',
        errorMessage: '',
      },
    )
  }
  if (TransactionTypeEnum.OTHER_INCOME === currentCategory) {
    rows.push(
      {
        id: 'isCapitalize',
        validityStatus: 'valid',
        type: 'string',
        isEditable: true,
        field: 'Capitalized',
        value:
          transaction?.dataMap?.isCapitalize != null
            ? String(transaction.dataMap.isCapitalize)
            : 'false',
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'loanFeeType',
        validityStatus: 'valid',
        type: 'string',
        isEditable: true,
        field: 'Loan Fee Type',
        value: resolveLoanFeeType(transaction?.dataMap?.loanFeeType),
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'exitFee',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Loan Exit Fee',
        value: String(
          transaction?.dataMap?.exitFee != null
            ? Number(transaction.dataMap.exitFee)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'exitFeeReceivable',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Loan Exit Fee Receivable',
        value: String(
          transaction?.dataMap?.exitFeeReceivable != null
            ? Number(transaction.dataMap.exitFeeReceivable)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'modificationFee',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Loan Modification Fee',
        value: String(
          transaction?.dataMap?.modificationFee != null
            ? Number(transaction.dataMap.modificationFee)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'miscellaneousFee',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Loan Miscellaneous Fee',
        value: String(
          transaction?.dataMap?.miscellaneousFee != null
            ? Number(transaction.dataMap.miscellaneousFee)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
    )
  }
  if (TransactionTypeEnum.CASH_BALANCE_FIELD_ADJUSTMENT === currentCategory) {
    const balanceToAdjustValue = resolveBalanceToAdjust(
      transaction?.dataMap?.balanceToAdjust,
    )
    rows.push({
      id: 'balanceToAdjust',
      validityStatus: balanceToAdjustValue ? 'valid' : 'invalid',
      type: 'string',
      isEditable: true,
      field: 'Balance To Adjust',
      value: balanceToAdjustValue,
      custody: '',
      trading: '',
      errorMessage: balanceToAdjustValue ? '' : 'Balance To Adjust is required',
    })
  }
  if (TransactionTypeEnum.COUPON === currentCategory) {
    rows.push(
      {
        id: 'prepaidInterest',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Loan Prepaid Interest',
        value: String(prepaidInterest ? prepaidInterest : 0.0),
        custody: custodyPrepaidInterest?.toString(),
        trading: tradingPrepaidInterest?.toString(),
        errorMessage: '',
      },
      {
        id: 'delinquencyFee',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Loan Delinquency Fee',
        value: String(
          transaction?.dataMap?.delinquencyFee != null
            ? Number(transaction.dataMap.delinquencyFee)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'interestBearingInterestReserve',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Interest Reserve (Interest Bearing)',
        value: String(
          transaction?.dataMap?.interestBearingInterestReserve != null
            ? Number(transaction.dataMap.interestBearingInterestReserve)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'interestBearingPrincipalReserve',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Principal Reserve (Interest Bearing)',
        value: String(
          transaction?.dataMap?.interestBearingPrincipalReserve != null
            ? Number(transaction.dataMap.interestBearingPrincipalReserve)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'nonInterestBearingInterestReserve',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Interest Reserve (Non-Interest Bearing)',
        value: String(
          transaction?.dataMap?.nonInterestBearingInterestReserve != null
            ? Number(transaction.dataMap.nonInterestBearingInterestReserve)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'nonInterestBearingPrincipalReserve',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Principal Reserve (Non-Interest Bearing)',
        value: String(
          transaction?.dataMap?.nonInterestBearingPrincipalReserve != null
            ? Number(transaction.dataMap.nonInterestBearingPrincipalReserve)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
    )
  }
  if (TransactionTypeEnum.PAYDOWN === currentCategory) {
    rows.push(
      {
        id: 'exitFee',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Loan Exit Fee Received',
        value: String(
          transaction?.dataMap?.exitFee != null
            ? Number(transaction.dataMap.exitFee)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
      {
        id: 'prepaymentPenaltyFee',
        validityStatus: 'valid',
        type: 'number',
        isEditable: true,
        field: 'Loan Prepayment Penalty Fee',
        value: String(
          transaction?.dataMap?.prepaymentPenaltyFee != null
            ? Number(transaction.dataMap.prepaymentPenaltyFee)
            : 0.0,
        ),
        custody: '',
        trading: '',
        errorMessage: '',
      },
    )
  }
  return rows
}

/**
 * Function to generate advanced rows when prepopulate for cash enteries
 */
export function generateAdvancedDataRowsForPrepopulated(
  prePopulated: CashEntryInputModel,
): CashEntryModel[] {
  const rows: CashEntryModel[] = []
  rows.push({
    id: 'prepaidInterest',
    validityStatus: 'valid',
    type: 'number',
    isEditable: true,
    field: 'Loan Prepaid Interest',
    value: String(
      prePopulated.prepaidInterest != null
        ? Number(prePopulated.prepaidInterest)
        : 0.0,
    ),
    custody: '',
    trading: '',
    errorMessage: '',
  })
  return rows
}

/**
 * extra Collateral fields
 */
export function getCollateralBroker(collateralValue?: string): CashEntryModel {
  return {
    id: 'collateralBroker',
    validityStatus: 'valid',
    type: 'string',
    isEditable: true,
    field: 'Collateral Broker',
    value: collateralValue ?? '',
    custody: '',
    trading: '',
    errorMessage: '',
  }
}

/**
 * Fetch Currency Values fro external transaction
 */
export function fetchExternalTransactionCurrencyValues(
  externalTransaction: ExternalUnmatchedTransaction,
  currencyList: Currency[],
) {
  let currency = getCurrencyFromId(
    externalTransaction?.currencyId,
    currencyList,
  )
  if (!currency) {
    currency = getDefaultCurrency()
  }
  const cusipCwValue = CURRENCY_ONLY_TRANSACTION_TYPES.includes(
    TransactionTypeEnum[externalTransaction.tranType],
  )
    ? currency?.cusip
    : externalTransaction?.assetId

  return [cusipCwValue, currency] as const
}

/**
 * Fetch Currency Values fro external transaction
 */
export function fetchExternalTransactionSecMasterCurrencyValues(
  externalTransaction: ExternalUnmatchedTransaction,
  currencyList: Currency[],
) {
  let currency = getCurrencyFromId(
    externalTransaction?.secMasterCurrencyId,
    currencyList,
  )
  if (!currency) {
    currency = getDefaultCurrency()
  }
  const cusipCwValue = CURRENCY_ONLY_TRANSACTION_TYPES.includes(
    TransactionTypeEnum[externalTransaction.tranType],
  )
    ? currency?.cusip
    : externalTransaction?.assetId

  return [cusipCwValue, currency] as const
}

/**
 * Function to create transaction from custody external transaction
 */
export function createCashEntryMatchFromTransactionMatch(
  externalTransaction: TransactionMatch,
  accountName: string,
  currencyList: Currency[],
): CashEntryMatchRow {
  return {
    account: accountName,
    category: externalTransaction.tranType
      ? TransactionTypeEnum[externalTransaction.tranType]
      : '',
    entryDate: externalTransaction.entryDate,
    settleDate: externalTransaction.settleDate,
    postDate: externalTransaction.postDate,
    cusip: externalTransaction.assetId ?? '',
    currency:
      getCurrencyFromId(externalTransaction.currencyId, currencyList)?.code ??
      '',
    amount: externalTransaction.amount.toString(),
    inventoryType: externalTransaction.inventoryType ?? '',
    isCapitalize: externalTransaction.isCapitalize
      ? String(externalTransaction.isCapitalize)
      : '',
  }
}
